import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import type { UserDetails } from '../../api/admin';
import PortalAvatar, { getPortalAvatarLabel } from '../../components/profile/PortalAvatar';
import { useApi } from '../../context/ApiProvider';
import { useAuth } from '../../context/AuthProvider';
import { cyberGlassHairline, cyberGlassSurface, cyberGlassTextMuted, cyberGlassTextPrimary } from '../../styles/cyberGlass';

const Page = styled.section`
  max-width: 1080px;
  margin: 0 auto;
  padding: 20px;
  display: grid;
  gap: 14px;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const BackButton = styled.button`
  border: 1px solid ${cyberGlassHairline};
  border-radius: 999px;
  background: rgba(10, 15, 34, 0.44);
  color: ${cyberGlassTextPrimary};
  font-size: 0.78rem;
  font-weight: 900;
  line-height: 1;
  padding: 8px 12px;
  cursor: pointer;
`;

const Layout = styled.div`
  ${cyberGlassSurface};
  border: 1px solid ${cyberGlassHairline};
  border-radius: 18px;
  padding: 16px;
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 16px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const IdentityCard = styled.aside`
  border: 1px solid ${cyberGlassHairline};
  border-radius: 14px;
  background: rgba(8, 12, 24, 0.42);
  padding: 14px;
  display: grid;
  gap: 10px;
  align-content: start;
`;

const DisplayName = styled.h2`
  margin: 0;
  color: ${cyberGlassTextPrimary};
  font-size: 1.18rem;
  font-weight: 900;
  line-height: 1.25;
`;

const Email = styled.div`
  color: ${cyberGlassTextMuted};
  font-size: 0.84rem;
  font-weight: 700;
  overflow-wrap: anywhere;
`;

const RolePill = styled.span`
  display: inline-block;
  width: fit-content;
  border-radius: 999px;
  border: 1px solid ${cyberGlassHairline};
  background: rgba(8, 12, 24, 0.3);
  color: ${cyberGlassTextPrimary};
  font-size: 0.7rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 6px 10px;
`;

const Details = styled.div`
  border: 1px solid ${cyberGlassHairline};
  border-radius: 14px;
  background: rgba(8, 12, 24, 0.3);
  padding: 14px;
  display: grid;
  gap: 14px;
  align-content: start;
`;

const SectionTitle = styled.h3`
  margin: 0;
  color: ${cyberGlassTextPrimary};
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 900;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px 14px;
`;

const Field = styled.div`
  min-width: 0;
  display: grid;
  gap: 2px;
`;

const Label = styled.span`
  color: ${cyberGlassTextMuted};
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 800;
`;

const Value = styled.span`
  color: ${cyberGlassTextPrimary};
  font-size: 0.9rem;
  line-height: 1.3;
  font-weight: 800;
  overflow-wrap: anywhere;
`;

const Message = styled.div<{ $error?: boolean }>`
  border: 1px dashed ${({ $error }) => ($error ? 'rgba(255, 121, 121, 0.66)' : cyberGlassHairline)};
  border-radius: 12px;
  padding: 12px;
  color: ${({ $error }) => ($error ? 'rgba(255, 183, 183, 0.96)' : cyberGlassTextMuted)};
  font-size: 0.86rem;
  font-weight: 700;
`;

function asText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function formatTimestamp(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return new Date(value).toLocaleString();
  }
  return '--';
}

export default function UserProfilePage() {
  const { userRef } = useParams<{ userRef: string }>();
  const api = useApi();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const normalizedRef = useMemo(() => decodeURIComponent(userRef ?? '').trim(), [userRef]);

  useEffect(() => {
    if (!normalizedRef) {
      setProfile(null);
      setError('No profile reference was provided.');
      return;
    }

    const selfUid = asText(user?.uid);
    const selfEmail = asText(user?.email).toLowerCase();
    if (normalizedRef === selfUid || normalizedRef.toLowerCase() === selfEmail) {
      navigate('/dashboard/profile', { replace: true });
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      setProfile(null);

      try {
        let details: UserDetails | null = null;

        try {
          const direct = await api.get<UserDetails>(`/admin/users/${encodeURIComponent(normalizedRef)}`);
          details = direct.data;
        } catch {
          details = null;
        }

        if (!details) {
          const list = await api.get<{ users?: Array<{ id?: string; email?: string }> }>('/admin/users', {
            params: { q: normalizedRef, page: 0 },
          });
          const users = Array.isArray(list.data?.users) ? list.data.users : [];
          const lowerRef = normalizedRef.toLowerCase();
          const matched = users.find((row) => {
            const rowId = asText(row.id);
            const rowEmail = asText(row.email).toLowerCase();
            return rowId === normalizedRef || rowEmail === lowerRef;
          });
          if (matched?.id) {
            const exact = await api.get<UserDetails>(`/admin/users/${encodeURIComponent(matched.id)}`);
            details = exact.data;
          }
        }

        if (!details) {
          throw new Error('Could not find that user profile.');
        }

        if (!cancelled) {
          setProfile(details);
        }
      } catch (loadError: any) {
        if (!cancelled) {
          setError(loadError?.message ?? 'Failed to load user profile.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [api, navigate, normalizedRef, user?.email, user?.uid]);

  const displayName = getPortalAvatarLabel({
    firstName: profile?.firstName ?? null,
    lastName: profile?.lastName ?? null,
    name: profile?.name ?? null,
    email: profile?.email ?? null,
  });

  return (
    <Page>
      <Header>
        <BackButton type="button" onClick={() => navigate(-1)}>
          Back
        </BackButton>
      </Header>

      {loading ? <Message>Loading profile...</Message> : null}
      {!loading && error ? <Message $error>{error}</Message> : null}

      {!loading && !error && profile ? (
        <Layout>
          <IdentityCard>
            <PortalAvatar
              uid={profile.id}
              email={profile.email}
              photoURL={profile.photoURL}
              firstName={profile.firstName}
              lastName={profile.lastName}
              name={profile.name}
              size={72}
            />
            <DisplayName>{displayName}</DisplayName>
            <Email>{profile.email}</Email>
            <RolePill>{profile.role}</RolePill>
          </IdentityCard>

          <Details>
            <SectionTitle>Profile Details</SectionTitle>
            <Grid>
              <Field>
                <Label>First name</Label>
                <Value>{asText(profile.firstName) || '--'}</Value>
              </Field>
              <Field>
                <Label>Last name</Label>
                <Value>{asText(profile.lastName) || '--'}</Value>
              </Field>
              <Field>
                <Label>User type</Label>
                <Value>{asText(profile.userType) || '--'}</Value>
              </Field>
              <Field>
                <Label>Contact phone</Label>
                <Value>{asText(profile.contactPhone) || '--'}</Value>
              </Field>
              <Field>
                <Label>Team</Label>
                <Value>{asText(profile.team) || '--'}</Value>
              </Field>
              <Field>
                <Label>Title</Label>
                <Value>{asText(profile.title ?? profile.agentTitle) || '--'}</Value>
              </Field>
              <Field>
                <Label>Supervisor</Label>
                <Value>{asText(profile.supervisorName) || '--'}</Value>
              </Field>
              <Field>
                <Label>Business name</Label>
                <Value>{asText(profile.businessName) || '--'}</Value>
              </Field>
              <Field>
                <Label>Point of contact</Label>
                <Value>{asText(profile.pointOfContact) || '--'}</Value>
              </Field>
              <Field>
                <Label>Created</Label>
                <Value>{formatTimestamp(profile.createdAt)}</Value>
              </Field>
              <Field>
                <Label>Last login</Label>
                <Value>{formatTimestamp(profile.lastLoginAt)}</Value>
              </Field>
            </Grid>
            <Field>
              <Label>Bio</Label>
              <Value>{asText(profile.bio) || '--'}</Value>
            </Field>
          </Details>
        </Layout>
      ) : null}
    </Page>
  );
}
